import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Chip,
  useTheme,
  CircularProgress,
  Alert 
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import PaymentModal from '../components/payment/PaymentModal';
import CustomerServiceModal from '../components/contact/CustomerServiceModal';
import LoginModal from '../components/Common/LoginModal';
import { fetchUserAvailableSkus, fetchPublicSkus } from '../store/actions/orderAction';
import { checkAuthStatus } from '../store/actions/authActions';

const Pricing = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [customerServiceModalOpen, setCustomerServiceModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);

  // Redux状态
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { userAvailableSkus, publicSkus } = useSelector((state) => state.order);

  const handleOpenPaymentModal = (sku) => {
    // 检查用户是否已登录
    if (!isAuthenticated || !user) {
      // 未登录用户弹出登录弹窗
      setSelectedSku(sku); // 保存选中的套餐，登录成功后直接打开支付弹窗
      setLoginModalOpen(true);
      return;
    }
    
    setSelectedSku(sku);
    setPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedSku(null);
  };

  const handleOpenCustomerServiceModal = () => {
    setCustomerServiceModalOpen(true);
  };

  const handleCloseCustomerServiceModal = () => {
    setCustomerServiceModalOpen(false);
  };

  const handleCloseLoginModal = () => {
    setLoginModalOpen(false);
    setSelectedSku(null); // 关闭登录弹窗时清除选中的套餐
  };

  const handleLoginSuccess = (userData) => {
    // 登录成功后的处理
    //console.log('登录成功:', userData);
    setLoginModalOpen(false);
    
    // 更新认证状态
    dispatch(checkAuthStatus());
    
    // 只有当用户之前点击了充值按钮（有选中的套餐）时，才在登录成功后打开支付弹窗
    /*if (selectedSku) {
      // 延迟一小段时间确保认证状态更新完成
      setTimeout(() => {
        setPaymentModalOpen(true);
      }, 500);
    }*/
    // 如果没有选中套餐，说明用户是直接登录的，不弹出支付弹窗
    
    // 登录成功后重新获取用户套餐数据
    if (userData && userData.userId) {
      dispatch(fetchUserAvailableSkus(userData.userId))
        .then(result => {
          //console.log('登录后获取用户套餐成功:', result);
        })
        .catch(error => {
          //console.error('登录后获取用户套餐失败:', error);
        });
    }
  };

  // 数据转换函数：将服务端SKU数据转换为前端展示格式
  const transformSkuToDisplayFormat = (sku) => {
    // 翻译每页消耗点数（应与后端AI5.0翻译商品的points_per_page保持一致）
    const TRANSLATION_POINTS_PER_PAGE = 2;
    
    // 计算平均每页价格（优先使用服务端计算好的值，fallback使用本地计算）
    const averagePagePrice = sku.averagePagePrice || (sku.sku_price / (sku.sku_total_points / TRANSLATION_POINTS_PER_PAGE));
    
    // 计算折扣百分比
    let discountText = '0%优惠';
    if (sku.discount > 0) {
      discountText = `${Math.round(sku.discount)}%优惠`;
    }

    return {
      id: sku._id,
      sku_id: sku.sku_id, // 保留原始sku_id用于API调用
      name: sku.sku_name,
      sku_name: sku.sku_name, // 保留原始字段
      price: `¥${sku.sku_price}`,
      sku_price: sku.sku_price, // 保留原始字段
      originalPoints: sku.sku_original_points,
      sku_original_points: sku.sku_original_points, // 保留原始字段
      bonusPoints: sku.sku_bonus_points,
      sku_bonus_points: sku.sku_bonus_points, // 保留原始字段
      totalPoints: sku.sku_total_points,
      sku_total_points: sku.sku_total_points, // 保留原始字段
      description: sku.description,
      features: sku.features || [],
      buttonText: '立即充值',
      buttonVariant: sku.popular ? 'contained' : 'outlined',
      popular: sku.popular,
      discount: discountText,
      isCustom: false,
      averagePagePrice: averagePagePrice.toFixed(2)
    };
  };

  // 企业定制套餐（固定显示）
  const customPlan = {
    name: '企业定制',
    price: '联系客服',
    originalPoints: '定制',
    bonusPoints: '更多',
    totalPoints: '海量',
    description: '量大从优，专属定制方案',
    features: [
      '大量充值更优惠',
      '专属定制方案',
      '企业级服务保障',
      '专属客户经理',
      '7×24小时支持',
      '数据安全保障',
      '发票开具服务'
    ],
    buttonText: '联系客服',
    buttonVariant: 'outlined',
    popular: false,
    discount: '最高优惠',
    isCustom: true
  };

  // 组合所有套餐数据
  const getAllPlans = () => {
    /*console.log('获取套餐数据调试信息:', {
      isAuthenticated,
      user,
      userAvailableSkus: userAvailableSkus.data,
      publicSkus: publicSkus.data,
      userLoading: userAvailableSkus.loading,
      publicLoading: publicSkus.loading,
      userError: userAvailableSkus.error,
      publicError: publicSkus.error
    });*/
    
    // 根据登录状态选择数据源
    const skuData = isAuthenticated && user ? userAvailableSkus.data : publicSkus.data;
    const transformedSkus = skuData.map(transformSkuToDisplayFormat);
    
    //console.log('转换后的SKU数据:', transformedSkus);
    
    // 确保每行最多4个卡片，如果超过4个，将企业定制卡片放在适当位置
    const plans = [...transformedSkus, customPlan];
    
    //console.log('最终套餐列表:', plans);
    
    return plans;
  };

  // 获取套餐数据
  useEffect(() => {
    //console.log('useEffect 触发:', { isAuthenticated, user: user ? { userId: user.userId, userType: user.userType } : null });
    
    if (isAuthenticated && user && user.userId) {
      // 已登录用户：获取用户可用套餐
      //console.log('开始获取用户套餐, userId:', user.userId);
      dispatch(fetchUserAvailableSkus(user.userId))
        .then(result => {
          //console.log('获取用户套餐成功:', result);
        })
        .catch(error => {
          //console.error('获取用户套餐列表失败:', error);
        });
    } else {
      // 未登录用户：获取公开套餐列表
      //console.log('用户未登录，获取公开套餐列表');
      dispatch(fetchPublicSkus())
        .then(result => {
          //console.log('获取公开套餐成功:', result);
        })
        .catch(error => {
          //console.error('获取公开套餐列表失败:', error);
        });
    }
  }, [isAuthenticated, user, dispatch]);

  // 渲染加载状态
  const isLoading = isAuthenticated && user ? userAvailableSkus.loading : publicSkus.loading;
  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          正在加载套餐信息...
        </Typography>
      </Box>
    );
  }

  // 渲染错误状态
  const currentError = isAuthenticated && user ? userAvailableSkus.error : publicSkus.error;
  if (currentError) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          加载套餐信息失败: {currentError}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              if (isAuthenticated && user) {
                dispatch(fetchUserAvailableSkus(user.userId));
              } else {
                dispatch(fetchPublicSkus());
              }
            }}
          >
            重新加载
          </Button>
        </Box>
      </Box>
    );
  }

  const plans = getAllPlans();

  return (
    <Box sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <PaymentIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 2
          }}
        >
          充值套餐
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          翻译服务按页消耗点数，充值越多平均每页价格越低，点数永不过期
        </Typography>
      </Box>

      {/* 定价卡片 */}
      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid item xs={12} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                position: 'relative',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: plan.popular ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0, 0, 0, 0.12)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    px: 3,
                    py: 0.8,
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '12px 0 24px 0',
                    minWidth: '100px',
                    textAlign: 'center',
                    zIndex: 2
                  }}
                >
                  推荐
                </Box>
              )}
              
              {/* 优惠标签 */}
              <Chip
                label={plan.discount}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: '#ff4757',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
              
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                  {plan.name}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="h3" 
                    component="span" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.primary.main 
                    }}
                  >
                    {plan.price}
                  </Typography>
                </Box>

                {/* 点数信息 */}
                {!plan.isCustom && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: theme.palette.primary.main + '10', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      充值 {plan.originalPoints} 点 + 赠送 {plan.bonusPoints} 点
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      总共 {plan.totalPoints} 点
                    </Typography>
                  </Box>
                )}

                {/* 价格优势突出显示 */}
                {!plan.isCustom && (
                  <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.success.main}20`
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      💡 翻译计费说明
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                      每页消耗1点 = 实际每页仅需 {plan.averagePagePrice}¥
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      充值越多，平均每页价格越低
                    </Typography>
                  </Box>
                )}
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3 }}
                >
                  {plan.description}
                </Typography>
                
                <Button
                  variant={plan.buttonVariant}
                  fullWidth
                  size="large"
                  onClick={() => {
                    if (plan.isCustom) {
                      handleOpenCustomerServiceModal();
                    } else {
                      handleOpenPaymentModal(plan);
                    }
                  }}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600
                  }}
                >
                  {plan.buttonText}
                </Button>
                
                <List sx={{ textAlign: 'left' }}>
                  {plan.features.map((feature, featureIndex) => (
                    <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: 20, 
                            color: theme.palette.success.main 
                          }} 
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 常见问题 */}
      <Box sx={{ mt: 24, mb: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 8, fontWeight: 600, color: theme.palette.primary.main }}>
          常见问题
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, textAlign: 'left', borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                实际翻译怎么计费？
              </Typography>
              <Typography variant="body2" color="text.secondary">
                我们按照标准 A4 页面计算，图片按1页计算，PDF等文档按实际页数计算。系统会自动识别并计算您上传文档的页数。
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, textAlign: 'left', borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                支持哪些文档格式？
              </Typography>
              <Typography variant="body2" color="text.secondary">
                我们支持 PDF、图片（JPG/PNG）等主流格式。
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, textAlign: 'left', borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                翻译质量如何保证？
              </Typography>
              <Typography variant="body2" color="text.secondary">
                我们采用先进的 AI 翻译技术，确保翻译的准确性和专业性。认证翻译结合人工审核认证提供最高级别的翻译质量保证。
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, textAlign: 'left', borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                可以随时购买或变更点数包套餐吗？
              </Typography>
              <Typography variant="body2" color="text.secondary">
                是的，您可以随时购买或变更点数包，购买立即生效。点数包作为虚拟商品，使用期限为永久有效，不支持退款。
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* 充值弹窗 */}
      <PaymentModal 
        open={paymentModalOpen} 
        onClose={handleClosePaymentModal}
        selectedSku={selectedSku}
      />

      {/* 客服弹窗 */}
      <CustomerServiceModal 
        open={customerServiceModalOpen} 
        onClose={handleCloseCustomerServiceModal} 
      />

      {/* 登录弹窗 */}
      <LoginModal 
        open={loginModalOpen} 
        onClose={handleCloseLoginModal}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};

export default Pricing; 